import { useState, useRef, useCallback } from 'react';
import { Camera, X, RotateCcw, Check } from 'lucide-react';

interface CameraCaptureProps {
  onCapture: (photoDataUrl: string) => void;
  onClose: () => void;
}

export function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to data URL
    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(photoDataUrl);
    stopCamera();
  }, [stopCamera]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    startCamera();
  }, [startCamera]);

  const confirmPhoto = useCallback(() => {
    if (capturedPhoto) {
      onCapture(capturedPhoto);
      onClose();
    }
  }, [capturedPhoto, onCapture, onClose]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  // Start camera when component mounts
  useState(() => {
    startCamera();
  });

  // Cleanup on unmount
  useState(() => {
    return () => {
      stopCamera();
    };
  });

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
      <div className="bg-sw-panel border border-sw-yellow rounded-sm p-4 max-w-md w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-black uppercase text-white">
            {capturedPhoto ? 'Photo Captured' : 'Take Photo'}
          </h3>
          <button
            onClick={handleClose}
            className="p-1 text-sw-muted hover:text-sw-yellow"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error ? (
          <div className="text-center py-8">
            <Camera className="w-12 h-12 text-sw-muted mx-auto mb-4" />
            <p className="pen-error mb-4">{error}</p>
            <button
              onClick={startCamera}
              className="pen-btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : capturedPhoto ? (
          <div className="space-y-4">
            <div className="relative">
              <img
                src={capturedPhoto}
                alt="Captured photo"
                className="w-full h-64 object-cover rounded-sm border border-sw-border"
              />
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={retakePhoto}
                className="pen-btn-ghost flex-1"
              >
                <RotateCcw className="w-4 h-4" />
                Retake
              </button>
              <button
                onClick={confirmPhoto}
                className="pen-btn-primary flex-1"
              >
                <Check className="w-4 h-4" />
                Use Photo
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-64 bg-black rounded-sm object-cover border border-sw-yellow"
              />
              {!isStreaming && (
                <div className="absolute inset-0 flex items-center justify-center bg-black rounded-sm">
                  <Camera className="w-12 h-12 text-sw-muted" />
                </div>
              )}
            </div>
            <button
              onClick={capturePhoto}
              disabled={!isStreaming}
              className="pen-btn-primary w-full"
            >
              <Camera className="w-4 h-4" />
              Take Photo
            </button>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}